import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { auditLogService } from '@/services/auditLogService';
import { complianceService } from './complianceService';

export type FormFieldType = 'text' | 'number' | 'date' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'photo' | 'reference' | 'calculated';

export interface FormField {
  id?: string;
  label: string; // English (primary)
  label_es?: string; // Spanish translation
  type: FormFieldType;
  order: number;
  required?: boolean;
  placeholder?: string;
  options?: string[]; // For select, radio, checkbox types
  defaultValue?: string;
  itemReferenceValues?: Record<string, string | number>; // For 'reference' type - stores per-item values (e.g., {"Wine": 50, "Beer": 100})
  formula?: string; // For 'calculated' type - stores the formula expression (e.g., "Par Level - Current Inventory")
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
}

export interface FormVariable {
  name: string;
  possibleValues: string[]; // List of allowed values for this variable
}

import type { ReferenceDocument } from './complianceService';

export interface TableItemsDefinition {
  type: 'static' | 'variable'; // Static list or from variable
  items?: string[]; // ['Wine', 'Liquor', 'Beer'] for static type - English (primary)
  items_es?: string[]; // Spanish translation - parallel array to items
  variableName?: string; // Variable name that provides item list (for variable type)
}

export interface Form {
  id?: string;
  name: string; // English (primary)
  name_es?: string; // Spanish translation
  description?: string;
  description_es?: string; // Spanish translation of description
  layoutType?: 'standard' | 'table'; // Form layout type: standard (vertical fields) or table (items as rows)
  fields: FormField[]; // For standard layout - vertical fields
  // For table layout:
  itemsDefinition?: TableItemsDefinition; // How items are defined (static list or variable)
  tableFields?: FormField[]; // Fields that apply to each item (become columns)
  headerFields?: FormField[]; // Fields at top (like Date) that apply once per submission
  variables?: FormVariable[]; // Variable definitions with possible values
  templateId?: string; // Associated compliance template ID
  locationIds?: string[]; // Array of location IDs - if empty/null/undefined, available for all locations (backward compatible)
  referenceDocuments?: ReferenceDocument[]; // Reference documents for the form
  organizationId?: string; // Multi-tenant support
  createdAt?: any;
  updatedAt?: any;
}

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
// Preserves Firestore FieldValue objects (like serverTimestamp())
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  // Preserve Firestore FieldValue objects (they have _methodName property)
  if (typeof obj === 'object' && obj._methodName) {
    return obj; // Don't process FieldValue objects
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

export const formService = {
  // Get all forms for an organization
  getAllForms: async (organizationId?: string) => {
    try {
      const formsRef = collection(db, 'forms');
      
      // Build query with organization filter if provided
      let q;
      if (organizationId) {
        // Filter by organizationId without orderBy to avoid index requirement
        q = query(
          formsRef,
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(formsRef, orderBy('name'));
      }
      
      const querySnapshot = await getDocs(q);
      const forms: Form[] = [];
      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        const form = { id: doc.id, ...docData } as Form;
        forms.push(form);
      });
      
      // Sort in memory after fetching to avoid index requirement
      if (organizationId) {
        forms.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      return { success: true, data: forms };
    } catch (error: any) {
      console.error('Error getting all forms:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single form by ID
  getForm: async (id: string) => {
    try {
      const formRef = doc(db, 'forms', id);
      const docSnap = await getDoc(formRef);
      if (docSnap.exists()) {
        const docData = docSnap.data();
        const formData = { id: docSnap.id, ...docData } as Form;
        return { success: true, data: formData };
      } else {
        return { success: false, error: 'Form not found' };
      }
    } catch (error: any) {
      console.error('Error getting form:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new form
  addForm: async (
    formData: Omit<Form, 'id'>, 
    organizationId?: string,
    userInfo?: { id: string; name: string; email: string }
  ) => {
    try {
      const formsRef = collection(db, 'forms');
      // Remove timestamps from formData if present (we'll set them explicitly after cleaning)
      const { createdAt: _, updatedAt: __, ...formDataWithoutTimestamps } = formData;
      const cleanedData = removeUndefined({
        ...formDataWithoutTimestamps,
        ...(organizationId && { organizationId }), // Add organizationId if provided
      });
      // Set timestamps after cleaning to ensure they're not processed by removeUndefined
      cleanedData.createdAt = serverTimestamp();
      cleanedData.updatedAt = serverTimestamp();
      const docRef = await addDoc(formsRef, cleanedData);
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'form_added',
          entityType: 'form',
          entityId: docRef.id,
          entityName: formData.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding form:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an existing form
  updateForm: async (
    id: string, 
    formData: Partial<Form>,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get current form for audit log
      let currentForm: Form | null = null;
      if (userInfo && organizationId) {
        const currentResult = await formService.getForm(id);
        if (currentResult.success && currentResult.data) {
          currentForm = currentResult.data;
        }
      }
      
      const formRef = doc(db, 'forms', id);
      // Remove updatedAt from formData if present (we'll set it explicitly after cleaning)
      const { updatedAt: _, ...formDataWithoutUpdatedAt } = formData;
      const cleanedData = removeUndefined(formDataWithoutUpdatedAt);
      // Always set updatedAt explicitly after cleaning to ensure it's not removed
      cleanedData.updatedAt = serverTimestamp();
      await updateDoc(formRef, cleanedData);
      
      // Log audit entries for changes
      if (userInfo && organizationId && currentForm) {
        // Check for compliance change (templateId)
        if (formData.templateId !== undefined && formData.templateId !== currentForm.templateId) {
          // Get compliance template names
          let oldComplianceName = 'Unknown';
          let newComplianceName = 'Unknown';
          
          if (currentForm.templateId) {
            const oldTemplateResult = await complianceService.getTemplate(currentForm.templateId);
            if (oldTemplateResult.success && oldTemplateResult.data) {
              oldComplianceName = oldTemplateResult.data.name;
            }
          }
          
          if (formData.templateId) {
            const newTemplateResult = await complianceService.getTemplate(formData.templateId);
            if (newTemplateResult.success && newTemplateResult.data) {
              newComplianceName = newTemplateResult.data.name;
            }
          }
          
          await auditLogService.log({
            action: 'form_compliance_changed',
            entityType: 'form',
            entityId: id,
            entityName: formData.name || currentForm.name,
            userId: userInfo.id,
            userName: userInfo.name,
            userEmail: userInfo.email,
            organizationId: organizationId,
            details: {
              oldComplianceId: currentForm.templateId,
              newComplianceId: formData.templateId,
              oldComplianceName: oldComplianceName,
              newComplianceName: newComplianceName,
            },
          });
        }
        
        // Log general update (if not just a compliance change)
        if (formData.templateId === undefined || formData.templateId === currentForm.templateId) {
          await auditLogService.log({
            action: 'form_updated',
            entityType: 'form',
            entityId: id,
            entityName: formData.name || currentForm.name,
            userId: userInfo.id,
            userName: userInfo.name,
            userEmail: userInfo.email,
            organizationId: organizationId,
            details: {
              oldValue: currentForm,
              newValue: { ...currentForm, ...cleanedData },
            },
          });
        }
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating form:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a form
  deleteForm: async (
    id: string,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get form info before deletion for audit log
      let formName = 'Unknown';
      if (userInfo && organizationId) {
        const formResult = await formService.getForm(id);
        if (formResult.success && formResult.data) {
          formName = formResult.data.name;
        }
      }
      
      const formRef = doc(db, 'forms', id);
      await deleteDoc(formRef);
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'form_deleted',
          entityType: 'form',
          entityId: id,
          entityName: formName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting form:', error);
      return { success: false, error: error.message };
    }
  },

  // Copy a form
  copyForm: async (id: string) => {
    try {
      const formRef = doc(db, 'forms', id);
      const docSnap = await getDoc(formRef);
      if (docSnap.exists()) {
        const originalData = docSnap.data() as Form;
        // Remove timestamps from original data (we'll set them explicitly)
        const { createdAt: _, updatedAt: __, ...originalDataWithoutTimestamps } = originalData;
        const newFormData: Omit<Form, 'id'> = {
          ...originalDataWithoutTimestamps,
          name: `${originalData.name} (Copy)`,
        };
        const cleanedData = removeUndefined(newFormData);
        // Set timestamps after cleaning
        cleanedData.createdAt = serverTimestamp();
        cleanedData.updatedAt = serverTimestamp();
        const formsRef = collection(db, 'forms');
        const newDocRef = await addDoc(formsRef, cleanedData);
        return { success: true, id: newDocRef.id };
      } else {
        return { success: false, error: 'Form not found' };
      }
    } catch (error: any) {
      console.error('Error copying form:', error);
      return { success: false, error: error.message };
    }
  },
};

